import { useState, useEffect } from "react";
import { X, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Auto-opens when a program reports sync.state === "needs_answer".
 * Walks the user through AW's security questions ONE AT A TIME (sequential).
 * After all questions are answered, POSTs them to /aw/accounts/{id}/answer
 * and the dashboard polling picks up the resumed sync.
 */
export default function MfaPromptModal({ program, onClose, onSubmitted }) {
  // If AW didn't return any security questions but the row is in needs_answer
  // state (or the user clicked the pill manually), show a generic
  // verification-code prompt so the modal is still usable.
  const rawQuestions = program?.sync?.security_questions || [];
  const questions = rawQuestions.length > 0 ? rawQuestions : [{
    question: "Enter the verification code from your email or authenticator app to finish signing in.",
    code: "verification_code",
  }];
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setCurrentAnswer("");
    setError("");
  }, [program?.id, program?.sync?.security_questions]);

  if (!program) return null;
  // Always work on the first (and typically only) question per round.
  const currentQ = questions[0];
  if (!currentQ) return null;

  // AW asks one question per round. We always submit just the current
  // answer; if AW needs more answers, the dashboard polling will re-detect
  // sync.state==='needs_answer' and reopen this modal with the next prompt.
  async function handleSubmit() {
    if (!currentAnswer.trim()) {
      setError("Please enter an answer to continue.");
      return;
    }
    if (!program?.sync?.account_id) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/aw/accounts/${program.sync.account_id}/answer`, {
        answers: [{
          question: currentQ.question,
          code: currentQ.code,
          answer: currentAnswer.trim(),
        }],
      });
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to submit answer.";
      setError(msg);
      setCurrentAnswer("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 fade-in"
      style={{ background: "rgba(10,10,10,0.55)" }}
      data-testid="mfa-prompt-modal"
    >
      <div className="card w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          disabled={submitting}
          className="absolute top-3 right-3 text-neutral-400 hover:text-black"
          aria-label="Close"
          data-testid="mfa-close-btn"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
          <ShieldCheck size={12} /> Verification needed
        </div>
        <h2 className="font-display font-bold text-lg tracking-tight mt-1.5">
          {program.name}
        </h2>
        <p className="text-[12.5px] text-neutral-500 mt-0.5">
          {program.sync?.login}
        </p>

        <div className="mt-4 mb-2 text-[11px] text-neutral-500">
          Verification step
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
          className="mt-4 space-y-3"
          data-testid="mfa-form"
        >
          <label className="block">
            <span className="text-[12.5px] font-medium text-neutral-700 leading-relaxed block mb-2">
              {currentQ.question}
            </span>
            <input
              autoFocus
              type="text"
              className="input"
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              placeholder="Enter your answer…"
              autoComplete="off"
              spellCheck={false}
              disabled={submitting}
              data-testid="mfa-answer-input"
            />
          </label>

          {error && (
            <div
              className="text-[12px] p-2.5 rounded-[8px] flex items-start gap-2"
              style={{ background: "var(--bad-bg)", color: "var(--bad)" }}
              data-testid="mfa-error"
            >
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-start gap-2 text-[11px] text-neutral-500 pt-1">
            <ShieldCheck size={11} className="mt-0.5 shrink-0" />
            <span>
              We forward this answer directly to the program's website. Your
              session is cached for ~24h so you won't be asked again on the
              next sync.
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-black/5">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="btn btn-ghost text-[13px]"
              data-testid="mfa-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !currentAnswer.trim()}
              className="btn btn-primary text-[13px]"
              data-testid="mfa-submit-btn"
            >
              {submitting ? (
                <>
                  <span className="spinner" /> Submitting…
                </>
              ) : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
